import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Button,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { vehiclesAPI } from '../services/api';
import AppLayout from '../components/Layout/AppLayout';
import { useAuth } from '../components/Auth/AuthContext';
import DateRangePicker from '../components/common/DateRangePicker';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import '../styles/DashboardPage.css';

// Компонент для метрики
const MetricCard = ({ title, value, unit, icon, color, trend }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 3,
              bgcolor: `${color}20`,
              color: color,
              mr: 1,
            }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1 }}>
          {value}
          {unit && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {unit}
            </Typography>
          )}
        </Typography>
        {trend && (
          <Chip
            label={trend}
            size="small"
            sx={{
              bgcolor: trend.includes('+') ? 'success.light' : 'error.light',
              color: trend.includes('+') ? 'success.dark' : 'error.dark',
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Компонент для рейтинга
const RatingCard = ({ title, rating, maxRating = 10, color, description, onClick }) => {
  const percentage = (rating / maxRating) * 100;
  
  const getRatingLabel = (score) => {
    if (score >= 8) return 'Отлично';
    if (score >= 6) return 'Хорошо';
    if (score >= 4) return 'Удовлетворительно';
    return 'Требует внимания';
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        borderRadius: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4,
          borderColor: color,
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="h4" sx={{ color, mr: 0.5 }}>
            {rating.toFixed(1)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            / {maxRating}       
          </Typography>
          <Chip  
            label={getRatingLabel(rating)}
            size="small"
            sx={{
              bgcolor: `${color}20`,
              color: color,
              fontWeight: 600, 
              align: 'right'
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ flexGrow: 1, mr: 0.5 }}>
            
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: color,
                  borderRadius: 4,
                },
              }}
            />
            
          </Box>
          
        </Box>
        
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Компонент для отображения показателя рейтинга в строке
const RatingIndicator = ({ rating }) => {
  if (!rating) return null;

  return (
    <Box sx={{ textAlign: 'center', px: 0.5 }}>
      {/* Название показателя */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        display="block" 
        sx={{ 
          mb: 0.25, 
          fontSize: '0.65rem', 
          lineHeight: 1,
          minHeight: '1.3em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={rating.name}
      >
        {rating.name}
      </Typography>
      
      {/* Цветной кружок с рейтингом */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: rating.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          mb: 0.25,
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'white',
            fontWeight: 600,
            fontSize: '0.8rem',
          }}
        >
          {rating.points}
        </Typography>
      </Box>
      
      {/* Сырые данные */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        display="block" 
        sx={{ 
          fontSize: '0.6rem',
          lineHeight: 1,
          minHeight: '1.2em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={rating.raw_value !== undefined && rating.raw_value !== null 
          ? (typeof rating.raw_value === 'number' 
              ? rating.raw_value.toLocaleString('ru-RU') + (rating.unit || '')
              : String(rating.raw_value))
          : '0'}
      >
        {rating.raw_value !== undefined && rating.raw_value !== null 
          ? (typeof rating.raw_value === 'number' 
              ? rating.raw_value.toLocaleString('ru-RU')
              : rating.raw_value)
          : '0'}
        {rating.unit && (
          <span style={{ fontSize: '0.55rem', marginLeft: '0.05rem' }}>
            {rating.unit}
          </span>
        )}
      </Typography>
    </Box>
  );
};

// Компонент для отображения детальной информации о машине в виде строки
const VehicleRatingsRow = ({ vehicle, onBack }) => {
  if (!vehicle.ratings) return null;

  // Получаем все рейтинги в виде массива
  const ratingsArray = Object.values(vehicle.ratings);
  
  return (
    <Paper 
      variant="outlined"
      sx={{ 
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'background.default',
        borderColor: 'primary.light',
      }}
      onClick={onBack}
    >
      <Grid container alignItems="center" spacing={1}>
        {/* Заголовок и кнопка назад */}
        <Grid item xs={12} sm={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <IconButton 
              size="small" 
              onClick={onBack}
              sx={{ mr: 0.5, p: 0.2 }}
              title="Назад"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
                {vehicle.vehiclenumber}
              </Typography>
              <Typography variant="caption" color="text.secondary" lineHeight={1}>
                Детальные показатели
              </Typography>
            </Box>
          </Box>
        </Grid>
        
        {/* Отображение всех рейтингов в одной строке */}
        {ratingsArray.map((rating, index) => (
          <Grid item xs="auto" key={index} sx={{ flexGrow: 1, maxWidth: '80px', minWidth: '60px' }}>
            <RatingIndicator rating={rating} />
          </Grid>
        ))}
        
        {/* Кнопка назад справа */}
        <Grid item xs={12} sm={2.5} sx={{ textAlign: 'right' }}>
          
        </Grid>
      </Grid>
    </Paper>
  );
};

// Диалог со списком машин, отсортированных по выбранному показателю
const MetricVehiclesDialog = ({ open, onClose, metricKey, metricName, vehicles, data }) => {
  const theme = useTheme();
  
  if (!open || !vehicles || vehicles.length === 0) return null;
  
  // Сортируем машины по выбранному показателю
  const sortedVehicles = [...vehicles].sort((a, b) => {
    const ratingA = a.ratings?.[metricKey]?.points || 0;
    const ratingB = b.ratings?.[metricKey]?.points || 0;
    return ratingA - ratingB; // от меньшего к большему
  });

  // Получаем информацию о метрике
  const metricRating = data?.metric_ratings?.[metricKey];
  
  return (
    <Dialog 
  open={open} 
  onClose={onClose}
  maxWidth='lg' // Убираем ограничение по ширине
  fullWidth
  sx={{
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: 'lg', // Полная ширина
      m: 1
    }
  }}
>
  <DialogTitle sx={{ pb: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: metricRating?.color || theme.palette.primary.main,
            mr: 1.5,
          }}
        />
        <Typography variant="h6" component="span">
          {metricName}
        </Typography>
        {metricRating && (
          <Chip 
            label={`${metricRating.points}`} 
            size="small" 
            sx={{ 
              ml: 1.5,
              bgcolor: `${metricRating.color}20`,
              color: metricRating.color,
            }}
          />
        )}
      </Box>
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
    <Typography variant="body2" color="text.secondary">
      Список машин, отсортированных по показателю "{metricName}" (от худшего к лучшему)
    </Typography>
  </DialogTitle>
  
  <DialogContent dividers sx={{ p: 0, overflowX: 'auto' }}>
    <Box sx={{ 
      p: 2, 
      display: 'flex', 
      flexDirection: 'column',
      width: '100%'
    }}>
      {/* Заголовок таблицы - ВСЕ В ОДНУ СТРОКУ */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {/* Колонка "Машина" */}
          <Box sx={{ 
            width: '13.5%', 
            flexShrink: 0,
            minWidth: 170,
            maxWidth: 200,
          }}>
            <Typography variant="subtitle2" fontWeight={200}>
              Машина
            </Typography>
          </Box>
          
          {/* Все показатели в одну строку */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', overflowX: 'auto' }}>
            {/* Первая колонка - показатель по которому сортируем */}
            <Box sx={{ 
               width: '11.5%', 
            flexShrink: 0,
            minWidth: 120,
            maxWidth: 170,
              display: 'flex', 
              flex: 1,
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <Typography 
                variant="caption" 
                fontWeight={600}
                sx={{ 
                  color: metricRating?.color,
                  borderBottom: `2px solid ${metricRating?.color}`,
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                }}
              >
                {metricName}
              </Typography>
            </Box>
            
            {/* Остальные показатели */}
            {Object.keys(data?.metric_ratings || {})
              .filter(key => key !== metricKey)
              .map(key => {
                const metric = data?.metric_ratings?.[key];
                return (
                  <Box 
                    key={key} 
                    sx={{ 
                       width: '7%', 
			            flexShrink: 0,
			            minWidth: 80,
			            maxWidth: 120,
                      display: 'flex', 
                      flex: 1,
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        textAlign: 'center',
                      }}
                    >
                      {metric?.name}
                    </Typography>
                  </Box>
                );
              })}
            
            {/* Колонка с общим рейтингом */}
            <Box sx={{ 
              width: '11%', 
			            flexShrink: 0,
			            minWidth: 80,
			            maxWidth: 120,
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'flex-end'
            }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                }}
              >
                Общий рейтинг
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
      
      {/* Список машин - КАЖДАЯ МАШИНА В ОДНУ СТРОКУ */}
      <Stack spacing={1}>
        {sortedVehicles.map((vehicle, index) => {
          const selectedMetricRating = vehicle.ratings?.[metricKey];
          
          return (
            <Paper key={vehicle.agentid} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Информация о машине */}
                <Box sx={{  width: '13.5%', 
			            flexShrink: 0,
			            minWidth: 170,
			            maxWidth: 200
			            }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CarIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                    <div>
                      <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
                        {vehicle.vehiclenumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" lineHeight={1}>
                        Пробег: {vehicle.mileagecan ? vehicle.mileagecan.toLocaleString('ru-RU') : '0'} км
                      </Typography>
                    </div>
                  </Box>
                </Box>
                
                {/* Все рейтинги в одну строку */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
                  {/* Показатель по которому сортируем */}
                  <Box sx={{ 
                    width: '11.5%', 
		            flexShrink: 0,
		            minWidth: 120,
		            maxWidth: 170,
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    {selectedMetricRating ? (
                      <>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: selectedMetricRating.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 0.5,
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                            }}
                          >
                            {selectedMetricRating.points}
                          </Typography>
                        </Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: '0.6rem', textAlign: 'center' }}
                        >
                          {selectedMetricRating.raw_value !== undefined && selectedMetricRating.raw_value !== null 
                            ? (typeof selectedMetricRating.raw_value === 'number' 
                                ? selectedMetricRating.raw_value.toLocaleString('ru-RU')
                                : selectedMetricRating.raw_value)
                            : '0'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Нет данных
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Остальные показатели */}
                  {Object.keys(data?.metric_ratings || {})
                    .filter(key => key !== metricKey)
                    .map(key => {
                      const rating = vehicle.ratings?.[key];
                      return (
                        <Box 
                          key={key} 
                          sx={{ 
                           width: '11.5%', 
				            flexShrink: 0,
				            minWidth: 80,
				            maxWidth: 120,
	                      display: 'flex', 
	                      
                            flexDirection: 'column',
                            alignItems: 'center'
                          }}
                        >
                          {rating ? (
                            <>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  bgcolor: rating.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 0.5,
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {rating.points}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ fontSize: '0.6rem', textAlign: 'center' }}
                              >
                                {rating.raw_value !== undefined && rating.raw_value !== null 
                                  ? (typeof rating.raw_value === 'number' 
                                      ? rating.raw_value.toLocaleString('ru-RU')
                                      : rating.raw_value)
                                  : '0'}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  
                  {/* Общий рейтинг */}
                  <Box sx={{ 
                    width: '13%', 
			            flexShrink: 0,
			            minWidth: 80,
			            maxWidth: 120,
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: vehicle.overall_color,
                          mr: 0.5,
                        }}
                      />
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        sx={{ color: vehicle.overall_color }}
                      >
                        {vehicle.overall_score}/10
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  </DialogContent>
  
  <DialogActions sx={{ p: 1, position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
    <Button onClick={onClose} startIcon={<CloseIcon />}>
      Закрыть
    </Button>
    <Typography variant="caption" color="text.secondary">
      Всего машин: {sortedVehicles.length}
    </Typography>
  </DialogActions>
</Dialog>
  );
};

const DashboardPage = () => {
  const [dateRange, setDateRange] = useState(() => {
    const endTime = endOfDay(new Date());
    const startTime = startOfDay(subDays(new Date(), 7));
    return {
      start: startTime,
      end: endTime,
    };
  });
  
  const { user } = useAuth();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [expandedVehicles, setExpandedVehicles] = useState({});
  const [metricDialog, setMetricDialog] = useState({
    open: false,
    metricKey: null,
    metricName: null,
  });

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    setExpandedVehicles({});
    
    try {
      const startTimestamp = Math.floor(dateRange.start.getTime() / 1000);
      const endTimestamp = Math.floor(dateRange.end.getTime() / 1000);
      
      console.log('📅 Запрос данных за период:', {
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
        startTimestamp,
        endTimestamp,
        startHuman: dateRange.start.toLocaleString('ru-RU'),
        endHuman: dateRange.end.toLocaleString('ru-RU'),
      });
      
      const response = await vehiclesAPI.getVehicles({
        start_time: startTimestamp,
        end_time: endTimestamp,
      });
      
      console.log('✅ Данные получены');
      setData(response.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('❌ Ошибка при загрузке данных:', err);
      setError(err.response?.data?.message || 'Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleDetailsClick = (vehicleId, vehicle) => {
    console.log('Детали машины:', vehicle);
    setExpandedVehicles(prev => ({
      ...prev,
      [vehicleId]: !prev[vehicleId]
    }));
  };

  const handleBackClick = (vehicleId) => {
    setExpandedVehicles(prev => ({
      ...prev,
      [vehicleId]: false
    }));
  };

  const handleMetricCardClick = (metricKey, metricName) => {
    if (data && data.vehicles && data.vehicles.length > 0) {
      setMetricDialog({
        open: true,
        metricKey,
        metricName,
      });
    }
  };

  const handleCloseMetricDialog = () => {
    setMetricDialog({
      open: false,
      metricKey: null,
      metricName: null,
    });
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '—';
    return new Intl.NumberFormat('ru-RU').format(num);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '—';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    const parts = [];
    if (days > 0) parts.push(`${days}д`);
    if (hours > 0) parts.push(`${hours}ч`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}м`);
    
    return parts.join(' ');
  };

  return (
    <AppLayout>
      {/* Заголовок и управление */}
      <Box sx={{ 
      minHeight: '100vh',
      backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
      p: 3,
    }}>
      {/* Заголовок и управление */}
      <Box sx={{ 
        mb: 4,
       backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
        borderRadius: 2,
        p: 2,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 , spacing: 2}}>
          <div>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              📊 Сводка по парку
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Общая статистика
            </Typography>
          </div>
          
          <Box sx={{ display: 'flex', alignItems: 'left', gap: 2 }}>
            {lastUpdate && (
              <Typography variant="caption" color="text.secondary">
                Последнее обновление: {lastUpdate.toLocaleString('ru-RU')}
              </Typography>
            )}
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              disabled={loading}
              size="medium"
              loading={loading} 
            />
            
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? 'Обновление...' : 'Обновить'}
            </Button>
          </Box>
        </Box>
      </Box>

       {/* Состояние загрузки */}
        {loading && (
          <Box sx={{ 
            mb: 4,
            backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
            borderRadius: 2,
          }}>
          <LinearProgress sx={{ borderRadius: 2 }} />
        </Box>
      )}

      {/* Ошибка */}
        {error && !loading && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
            }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {data ? (
        <>
         {/* Основные метрики */}
          <Grid container spacing={2} sx={{ 
            mb: 3,
            backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
            borderRadius: 2,
            p: 2,
          }}>
            <Grid item xs={12} sm={6} md={3}>
              <RatingCard
                title="Общий рейтинг парка"
                rating={data.overall_rating?.points || 0}
                color={data.overall_rating?.color || theme.palette.success.main}
                description="Средневзвешенный рейтинг по всем машинам"
                onClick={() => {
                  // Для общего рейтинга сортируем по общему баллу
                  if (data.vehicles && data.vehicles.length > 0) {
                    setMetricDialog({
                      open: true,
                      metricKey: 'overall',
                      metricName: 'Общий рейтинг',
                    });
                  }
                }}
              />
            </Grid>
            
            <Grid item align='center' xs={12} sm={6} md={3}>
              <MetricCard
                title="Количество активных машин"
                value={formatNumber(data.total_vehicles)}
                icon={<CarIcon />}
                color={theme.palette.primary.main}
              />
            </Grid>
            
            <Grid item align='center' xs={12} sm={6} md={3}>
              <MetricCard
                title="Общий пробег"
                value={formatNumber(data.total_mileage)}
                unit="км"
                icon={<SpeedIcon />}
                color={theme.palette.info.main}
              />
            </Grid>
            
            <Grid item align='center' xs={12} sm={6} md={3}>
              <MetricCard
                title="Время работы"
                value={formatTime(data.total_engine_hours)}
                icon={<TimelineIcon />}
                color={theme.palette.success.main}
              />
            </Grid>
          </Grid>

          {/* Худший показатель */}
          <Grid container justifyContent="center" spacing={2} sx={{ 
            mb: 3,
            backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
            borderRadius: 2,
            p: 2,
          }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', borderRadius: 2, justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="subtitle2" align='center' color="text.secondary" gutterBottom>
                  ⚠️ Требует внимания
                </Typography>
                
                {data.worst_metric ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: '50%',
                          bgcolor: `${data.worst_metric.color}20`,
                          color: data.worst_metric.color,
                          mr: 2,
                        }}
                      >
                        {data.worst_metric.points >= 7 ? (
                          <CheckIcon />
                        ) : data.worst_metric.points >= 4 ? (
                          <WarningIcon />
                        ) : (
                          <ErrorIcon />
                        )}
                      </Box>
                      <div>
                        <Typography variant="h6">
                          {data.worst_metric.name}
                        </Typography>
                        <Typography variant="body3" color="text.secondary">
                          Самый низкий рейтинг
                        </Typography>
                      </div>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="h4" sx={{ 
                        color: data.worst_metric.color,
                        mr: 2,
                      }}>
                        {data.worst_metric.points}
                      </Typography>
                      <Typography variant="body2" align="center" color="text.secondary">
                        / 10 баллов
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography color="text.secondary">
                    Все показатели в норме
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

         {/* Рейтинги по показателям */}
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 3,
            backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
          }}>
            <Typography variant="h6" gutterBottom>
              📈 Рейтинги по инцидентам
            </Typography>
            
            <Grid container spacing={2}>
              {Object.entries(data.metric_ratings || {}).map(([key, metric]) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <RatingCard
                    title={metric.name}
                    rating={metric.points}
                    maxRating={10}
                    color={metric.color}
                    description={`${metric.points}/10 баллов`}
                    onClick={() => handleMetricCardClick(key, metric.name)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

           {/* Список машин */}
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
          }} >
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              🚗 Машины ({data.vehicles?.length || 0})
            </Typography>
            
            {data.vehicles && data.vehicles.length > 0 ? (
              <Stack spacing={1.5}>
                {data.vehicles.map((vehicle) => (
                  <div key={vehicle.agentid}>
                    {/* Обычный вид строки с машиной */}
                    {!expandedVehicles[vehicle.agentid] ? (
                      <Paper 
                        variant="outlined"
                        sx={{ 
                          p: 1.5,
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                        onClick={() => handleVehicleDetailsClick(vehicle.agentid, vehicle)}
                      >
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CarIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                              <div>
                                <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
                                  {vehicle.vehiclenumber}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" lineHeight={1}>
                                  ID: {vehicle.agentid}
                                </Typography>
                              </div>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6} sm={2}>
                            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>
                              Пробег
                            </Typography>
                            <Typography variant="body2" fontWeight={500} lineHeight={1.2}>
                              {formatNumber(vehicle.mileagecan)} км
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6} sm={2}>
                            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>
                              Время работы
                            </Typography>
                            <Typography variant="body2" lineHeight={1.2}>
                              {formatTime(vehicle.eng_time)}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6} sm={2}>
                            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>
                              Рейтинг
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: vehicle.overall_color,
                                  mr: 0.5,
                                }}
                              />
                              <Typography 
                                variant="body2" 
                                fontWeight={600}
                                sx={{ color: vehicle.overall_color }}
                                lineHeight={1.2}
                              >
                                {vehicle.overall_score}/10
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6} sm={3} sx={{ textAlign: 'right' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              
                              sx={{ 
                                borderRadius: 1.5,
                                py: 0.25,
                                px: 1.5,
                                fontSize: '0.75rem'
                              }}
                            >
                              Детали
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ) : (
                      /* Вид с рейтингами */
                      <VehicleRatingsRow 
                        vehicle={vehicle} 
                        onBack={() => handleBackClick(vehicle.agentid)}
                      />
                    )}
                  </div>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" align="center" py={4}>
                Нет данных о машинах
              </Typography>
            )}
          </Paper>
        </>
      ) : !loading && (
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center', 
          borderRadius: 2,
          backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0.7)),url("/pisuny.jpg")',
      backgroundSize: 'contain',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
        }}>
          <Typography color="text.secondary" gutterBottom>
            Нет данных для отображения
          </Typography>
          <Button 
            variant="contained" 
            onClick={loadData}
            sx={{ mt: 2 }}
          >
            Загрузить данные
          </Button>
        </Paper>
      )}

      {/* Диалог со списком машин по метрике */}
      <MetricVehiclesDialog
        open={metricDialog.open}
        onClose={handleCloseMetricDialog}
        metricKey={metricDialog.metricKey}
        metricName={metricDialog.metricName}
        vehicles={data?.vehicles || []}
        data={data}
      />
    </Box>
    </AppLayout>
  );
};

export default DashboardPage;
